import { Dialog } from '@factorialco/f0-react/dist/experimental'
import { Delete } from '@factorialco/f0-react/icons/app'
import {
  AnalyticsReportsPageQuery,
  DeleteAnalyticsReportDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type ReportNode = AnalyticsReportsPageQuery['analytics']['reportsConnection']['nodes'][number]

type Props = {
  itemToDelete: ReportNode | null
  setItemToDelete: (item: ReportNode | null) => void
}
const DeleteReportDialog = ({ itemToDelete, setItemToDelete }: Props) => {
  const { company } = useCurrentSession()
  const [deleteAnalyticsReport] = useMutation(DeleteAnalyticsReportDocument, {
    extractorFn: (data) => data.analyticsMutations.deleteAnalyticsReport,
  })
  const handleClose = () => setItemToDelete(null)
  const onDeleteReport = async (item: ReportNode) => {
    const result = await deleteAnalyticsReport({
      variables: {
        id: item.id,
        companyId: company.id,
      },
      update(cache) {
        cache.evict({
          id: cache.identify({ id: item.id, __typename: 'AnalyticsReport' }),
        })
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ai_reports.notifications.delete_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ai_reports.notifications.delete_success'),
      })
    }

    handleClose()
  }

  return (
    <Dialog
      onClose={handleClose}
      open={itemToDelete !== null}
      actions={{
        primary: {
          icon: Delete,
          label: i18n.t('common.delete'),
          onClick: () => {
            if (!itemToDelete) throw new Error('Missing item to delete')
            onDeleteReport(itemToDelete)
          },
          variant: 'critical',
        },
        secondary: {
          label: i18n.t('common.cancel'),
          onClick: handleClose,
        },
      }}
      header={{
        description: i18n.t('ai_reports.reports.delete_description', {
          reportName: itemToDelete?.name,
        }),
        title: i18n.t('ai_reports.reports.delete_title'),
        type: 'critical',
      }}
    />
  )
}
export default DeleteReportDialog
